import React, { useState, useEffect } from 'react';
import './App.css';
import StockDashboard from './components/StockDashboard';
import Leaderboard from './components/Leaderboard';
import GuestRegistration from './components/GuestRegistration';
import PersonalArea from './components/PersonalArea';
import EmployeeInterface from './components/EmployeeInterface';
import { io } from 'socket.io-client';

export interface Drink {
  id: number;
  name: string;
  basePrice: number;
  currentPrice: number;
  icon: string;
  purchases: number;
  priceHistory: { price: number; timestamp: string }[];
}

export interface Sale {
  id: number;
  drinkId: number;
  drinkName: string;
  quantity: number;
  priceAtSale: number;
  totalCost: number;
  timestamp: string;
  personId?: number;
  personName?: string;
}

export interface Person {
  id: number;
  name: string;
  totalSpent: number;
  totalSavings: number;
  purchaseCount: number;
  createdAt: string;
  qrCode?: string;
  qrCodeData?: string;
}

const App: React.FC = () => {
  // Navigation state
  const [view, setView] = useState<'dashboard' | 'registration' | 'personal' | 'leaderboard' | 'employee'>('dashboard');
  const [isRegistered, setIsRegistered] = useState(false);
  const [isEmployeeAuthenticated, setIsEmployeeAuthenticated] = useState(false);
  
  // Data state
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [salesHistory, setSalesHistory] = useState<Sale[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);

  // Check if user is registered on app load
  useEffect(() => {
    const guestId = localStorage.getItem('guestId');
    setIsRegistered(!!guestId);
  }, []);

  useEffect(() => {
    const newSocket = io('https://boersensaufen.fwne.de:1234');

    newSocket.on('initialData', (data: { drinks: Drink[], salesHistory: Sale[], persons: Person[] }) => {
      setDrinks(data.drinks);
      setSalesHistory(data.salesHistory);
      setPersons(data.persons || []);
    });

    newSocket.on('priceUpdate', (updatedDrinks: Drink[]) => {
      setDrinks(updatedDrinks);
    });

    newSocket.on('newSale', (sale: Sale) => {
      setSalesHistory(prev => [...prev, sale]);
    });

    newSocket.on('drinkAdded', (newDrink: Drink) => {
      setDrinks(prev => [...prev, newDrink]);
    });

    newSocket.on('drinkDeleted', (drinkId: number) => {
      setDrinks(prev => prev.filter(drink => drink.id !== drinkId));
    });

    newSocket.on('personAdded', (newPerson: Person) => {
      setPersons(prev => [...prev, newPerson]);
    });

    newSocket.on('personDeleted', (personId: number) => {
      setPersons(prev => prev.filter(person => person.id !== personId));
    });

    newSocket.on('personUpdated', (updatedPerson: Person) => {
      setPersons(prev => prev.map(person => 
        person.id === updatedPerson.id ? updatedPerson : person
      ));
    });

    newSocket.on('systemReset', () => {
      // Clear all client-side state
      setDrinks([]);
      setSalesHistory([]);
      setPersons([]);
      // Clear registration status on system reset
      localStorage.removeItem('guestId');
      localStorage.removeItem('guestName');
      setIsRegistered(false);
      setView('dashboard');
    });

    return () => {
      newSocket.close();
    };
  }, []);

  // API functions
  const handlePurchase = async (drinkId: number, quantity: number, personId?: number) => {
    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drinkId, quantity, personId })
      });
      
      if (!response.ok) {
        throw new Error('Purchase failed');
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Purchase error:', error);
      throw error;
    }
  };

  const handleAddDrink = async (name: string, basePrice: number, icon: string) => {
    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/drinks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, basePrice, icon })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add drink');
      }
      
      const newDrink = await response.json();
      return newDrink;
    } catch (error) {
      console.error('Add drink error:', error);
      throw error;
    }
  };

  const handleUpdateBasePrice = async (drinkId: number, basePrice: number) => {
    try {
      const response = await fetch(`https://boersensaufen.fwne.de:1234/api/drinks/${drinkId}/base-price`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ basePrice })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update base price');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Update base price error:', error);
      throw error;
    }
  };

  const handleDeleteDrink = async (drinkId: number) => {
    try {
      const response = await fetch(`https://boersensaufen.fwne.de:1234/api/drinks/${drinkId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete drink');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Delete drink error:', error);
      throw error;
    }
  };

  const handleAddPerson = async (name: string) => {
    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/persons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add person');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Add person error:', error);
      throw error;
    }
  };

  const handleDeletePerson = async (personId: number) => {
    try {
      const response = await fetch(`https://boersensaufen.fwne.de:1234/api/persons/${personId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete person');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Delete person error:', error);
      throw error;
    }
  };

  const handleRegistrationSuccess = (person: Person) => {
    setIsRegistered(true);
    // Don't redirect - stay in registration view to show QR code
  };

  const handleEmployeeAccess = () => {
    const password = prompt('Passwort für Mitarbeiterbereich:');
    if (password === '1234') {
      setIsEmployeeAuthenticated(true);
      setView('employee');
    } else {
      alert('Falsches Passwort!');
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'dashboard':
        return <StockDashboard drinks={drinks} salesHistory={salesHistory} />;
      case 'registration':
        return <GuestRegistration onRegistrationSuccess={handleRegistrationSuccess} />;
      case 'personal':
        return <PersonalArea isVisible={true} persons={persons} salesHistory={salesHistory} />;
      case 'leaderboard':
        return <Leaderboard persons={persons} salesHistory={salesHistory} />;
      case 'employee':
        if (isEmployeeAuthenticated) {
          return (
            <EmployeeInterface
              drinks={drinks}
              salesHistory={salesHistory}
              persons={persons}
              onPurchase={handlePurchase}
              onAddDrink={handleAddDrink}
              onUpdateBasePrice={handleUpdateBasePrice}
              onDeleteDrink={handleDeleteDrink}
              onAddPerson={handleAddPerson}
              onDeletePerson={handleDeletePerson}
              onBack={() => {
                setView('dashboard');
                setIsEmployeeAuthenticated(false);
              }}
            />
          );
        } else {
          // Should not happen since handleEmployeeAccess handles authentication
          return <StockDashboard drinks={drinks} salesHistory={salesHistory} />;
        }
      default:
        return <StockDashboard drinks={drinks} salesHistory={salesHistory} />;
    }
  };

  // Don't show main navigation in employee view
  if (view === 'employee' && isEmployeeAuthenticated) {
    return (
      <div className="App">
        <div 
          className="logo-background"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/Logo.jpg)',
            backgroundSize: '400px 400px',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
            opacity: 0.03,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="App">
      <nav className="navbar">
        <div className="navbar-brand">
          <img src="/Logo.jpg" alt="Jugendclub Niedereschach Logo" className="navbar-logo" />
          <h1>🍻 JCN Börsentrinken</h1>
        </div>
        <div className="nav-buttons">
          <button 
            className={view === 'dashboard' ? 'active' : ''} 
            onClick={() => setView('dashboard')}
          >
            📊 Börse
          </button>
          <button 
            className={view === 'registration' ? 'active' : ''} 
            onClick={() => setView('registration')}
          >
            📱 Registrierung
          </button>
          {isRegistered && (
            <button 
              className={view === 'personal' ? 'active' : ''} 
              onClick={() => setView('personal')}
            >
              👤 Mein Bereich
            </button>
          )}
          <button 
            className={view === 'leaderboard' ? 'active' : ''} 
            onClick={() => setView('leaderboard')}
          >
            🏆 Ranking
          </button>
          <button 
            className="employee-access-btn" 
            onClick={handleEmployeeAccess}
          >
            🔒 Mitarbeiter
          </button>
        </div>
      </nav>

      <div 
        className="logo-background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/Logo.jpg)',
          backgroundSize: '400px 400px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center',
          opacity: 0.03,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
      
      <main className="main-content">
        <div 
          className="main-logo-background"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/Logo.jpg)',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            opacity: 0.03,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        {renderContent()}
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <span className="club-name">Jugendclub Niedereschach</span>
          <span className="created-by">Created by Manuel C</span>
        </div>
      </footer>
    </div>
  );
};

export default App;